import React from "react";
import {GlobalContext} from "../../GlobalContext";
import {
    Button, ButtonGroup,
    Card,
    Col,
    Container, DropdownButton,
    Form, FormCheck, FormControl,
    FormGroup,
    FormLabel, InputGroup,
    ListGroup,
    ListGroupItem,
    Row,
    Table, ToggleButton
} from "react-bootstrap";
import moment from "moment-timezone";
import {Glyph} from "../common/Glyph";
import {ICD10Search} from "../common/ICD10Search";
import {SuperbillOrderItem} from "./SuperbillOrderItem";
import {ConfirmModal} from "../common/ConfirmModal";
import {nlToBr} from "../../utils/Strings";
import DropdownItem from "react-bootstrap/DropdownItem";
import PickList from "../common/PickList";
import CopyButton from "../common/CopyButton";
import SuperbillConfirmModal from "./SuperbillConfirmModal";
import {SuperbillDxInput} from "./SuperbillDxInput";
import {InterimSummaryModal} from "./InterimSummaryModal";

export class SuperbillForm extends React.Component {
    static contextType = GlobalContext;

    static AUTOSAVE_DELAY = 2000;
    autosaveTimer = null;

    constructor(props) {
        super(props);

        this.state = {
            patient: null,
            superbill: null,
            suggestedIcd10s: [],
            suggestedOrders: [],
            selectedIcd10s: [],
            chronicIcd10s: [],
            orders: [],
            orderMetadata: [],
            superbillRules: [],
            dirty: false,
            showLeaveModal: false,
            readOnly: true,
            internalProviders: [],
            saved: false,
            saving: false,
            validated: false,
            invalid: false,
            newPatientAppt: false,
            hmoDxConfirmed: false,
            showConfirmModal: false,
            noChronic: false,
            ambulatoryEEGStats: null,
            showFacetimeTracking: false,
            facetimeStartStr: "",
            facetimeEndStr: "",
            showInterimSummary: false,
            interimSummaries: [],
            interimSummariesLoading: false
        }
    }

    chronicAppts = ["NP"];
    followUpOptions = ["3 months", "4 months", "6 months", "1 year", "PRN", "After testing", "Second Opinion", "After seeing outside specialist(s)"];
    followUpOptionsIgnoreSuggestions = ["6 months", "1 year", "PRN", "Second Opinion"];

    componentDidUpdate(prevProps, prevState, snapshot) {
        // If we're supposed to be ignoring suggestions and there are some, clear them out
        if (this.shouldIgnoreSuggestedOrders() && this.state.suggestedOrders && this.state.suggestedOrders.length > 0) {
            this.setState({suggestedOrders: []});
        } else {
            let prevSb = prevState.superbill;
            if (prevSb && prevSb.followUp) {
                if (this.shouldIgnoreSuggestedOrders(prevSb.followUp) && !this.shouldIgnoreSuggestedOrders()) {
                    // We were ignoring before, and now we're not.  Pull suggestions
                    this.getSuggestions();
                }
            }
        }
    }

    validate = () => {
        let valid = document.getElementById("sb-form").checkValidity();
        let sb = this.state.superbill;
        if (sb) {
            valid &= (sb.providerId > 0);
            // valid &= !this.shouldShowChronic() || this.state.chronicIcd10s.length > 0 || this.state.noChronic;
        }
        this.setState({validated: true, invalid: !valid}, () => console.log(`invalid: ${this.state.invalid}`));

        return valid;
    }

    setDirty = (callback) => {
        this.setState({dirty: true, validated: false, invalid: false}, callback);
        if (this.autosaveTimer) {
            window.clearTimeout(this.autosaveTimer);
        }
        this.autosaveTimer = window.setTimeout(() => this.save(false), SuperbillForm.AUTOSAVE_DELAY);
    }

    componentDidMount() {
        this.setState({readOnly: !this.context.hasAnyRole(["System Admin", "Superbill Editor"])}, this.loadData);
    }

    getPatientId = () => {
        return this.getParam("patientId")
    }

    openInterimSummary = (e) => {
        if (e) e.preventDefault();
        const patientId = this.getPatientId();
        this.setState({showInterimSummary: true, interimSummariesLoading: true, interimSummaries: []});
        this.context.apiRequest("GET", `/patients/${patientId}/chartSummaries`)
            .then(d => {
                this.setState({interimSummaries: (d && d.data) || []});
            })
            .finally(() => this.setState({interimSummariesLoading: false}));
    }

    getParam = (name) => {
        let url= new URL(window.location.href);
        let params = new URLSearchParams(url.search);
        if (params.has(name)) return params.get(name);
        return null;
    }

    loadData = () => {
        let patientId = this.getPatientId();
        if (patientId) {
            let apptDate = this.getParam("apptDate");
            let sbid = this.getParam("id");

            this.context.apiRequest("GET", `/superbill?patientId=${patientId}${apptDate ? `&apptDate=${apptDate}` : ''}${sbid ? `&id=${sbid}` : ''}`)
                .then(d => {
                    if (d && d.data) {
                        let sb = d.data.superbill || this.defaultSuperbill(patientId, apptDate);
                        if (!sb.providerId && this.context.userProfile.providerId && this.context.hasAnyRole(["MD"])) {
                            sb.providerId = this.context.userProfile.providerId;
                        }

                        let patient = d.data.patient;
                        this.setState({
                            patient,
                            superbill: sb,
                            selectedIcd10s: sb ? sb.diagnoses || [] : [],
                            chronicIcd10s: sb ? sb.chronicIcd10s || [] : [],
                            orders: sb && sb.orders ? sb.orders : [],
                            newPatientAppt: patient.appointments && patient.appointments.some(a => a.reasonCode == 'NP'),
                            dirty: false, saved: true, saving: false,
                            facetimeStartStr: sb.facetimeStart ? moment(sb.facetimeStart).format("HH:mm") : "",
                            facetimeEndStr: sb.facetimeEnd ? moment(sb.facetimeEnd).format("HH:mm") : ""
                        }, () => {
                            this.context.apiRequest("GET", `/superbillMeta/rules?patientId=${patientId}`)
                                .then(resp => {
                                    if (resp && resp.data) {
                                        this.setState({superbillRules: resp.data}, () => this.evaluateSuperbillRules());
                                    }
                                });
                                
                            if (!this.state.readOnly && sb.status !== "SUBMITTED") {
                                this.getSuggestions();
                            }
                        });
                    }
                });
            this.context.cachedGet("/superbill/orderMetadata", 300)
                .then(d => {
                    if (d && d.data) {
                        this.setState({orderMetadata: d.data});
                    }
                });

            this.context.cachedGet("/providers/internal?active=1&users=true&roles=MD", 300)
                .then(d => {
                    if (d && d.data) {
                        this.setState({internalProviders: d.data});
                    }
                });

            this.context.apiRequest("GET", "/superbill/util/ambulatoryEEGStats")
                .then(resp => {
                    if (resp && resp.data) {
                        this.setState({ambulatoryEEGStats: resp.data});
                    }
                });

        }
    }

    defaultSuperbill = (patientId, apptDate) => {
        return {
            patientId,
            appointmentDate: apptDate,
            status: "NEW",
            prescriptionComments: "",
            generalComments: ""
        }
    }

    superbillFieldChanged = (e, overrideValue, callback) => {
        let fieldName = e.target.name;
        let value = overrideValue === undefined ? e.target.value : overrideValue;
        this.setSuperbillField(fieldName, value, callback);

    };

    setSuperbillField = (fieldName, value, callback) => {
        this.setState(prvState => ({
            superbill: {
                ...prvState.superbill,
                [fieldName]: value,
            }
        }), () => this.setDirty(callback));
    };

    shouldIgnoreSuggestedOrders = (fu) => {
        if (!fu) {
            let sb = this.state.superbill;
            if (sb) {
                fu = sb.followUp;
            }
        }
        if (fu) {
            return this.followUpOptionsIgnoreSuggestions.includes(fu);
        }
        return false;
    }

    goBack = (force) => {
        let loc = new URLSearchParams(window.location.href).get("_ref") || "/superbill";

        if (!this.state.dirty || force) {
            window.location.href = loc;
        } else {
            this.save(false)
                .then(() => {
                    window.location.href = loc;
                });
        }
    }

    getSuggestions = () => {
        let patientId = this.getPatientId();
        if (patientId) {
            let newIcds = this.state.selectedIcd10s.map(i => i.icd10Code).concat(this.state.chronicIcd10s.map(c => c.icd10Code)).join(',');
            this.context.apiRequest("GET", `/superbill/suggestions?patientId=${patientId}&newIcds=${newIcds}`)
                .then(d => {
                    if (d && d.data) {
                        let allOrders = [];
                        let allIcds = [];
                        for (const sugg of d.data) {
                            if (sugg.orders && sugg.orders.length > 0) {
                                allOrders = allOrders.concat(sugg.orders);
                            }
                            if (sugg.icds && sugg.icds.length > 0) {
                                allIcds = allIcds.concat(sugg.icds)
                            }
                        }
                        this.setState({suggestedIcd10s: allIcds, suggestedOrders: this.shouldIgnoreSuggestedOrders() ? [] : allOrders});
                    }
                })
        }
    }

    icd10Selected = (code, desc, fullCode) => {
        let p = this.state.patient;
        if (p.hmo && this.state.newPatientAppt && !this.state.hmoDxConfirmed) {
            this.context.confirm(() => this.doIcd10Selected(code, desc, fullCode),
                "Are you sure you want to add this diagnosis? Generally, for new HMO patients we only use the diagnoses provided in the referral.",
                "HMO Patient");
        } else {
            this.doIcd10Selected(code, desc, fullCode);
        }

    }

    chronicIcd10Selected = (code, desc, fullCode) => {
        if (!this.state.chronicIcd10s.some(i => i.icd10Code == code)) {
            let newList = [...this.state.chronicIcd10s];
            newList.push({icd10Code: code, description: desc, fullCode});
            this.setState({chronicIcd10s: newList}, () => this.setDirty(this.getSuggestions));
        }
    }

    doIcd10Selected = (code, desc, fullCode) => {
        // only do this if the code is not already in the list
        if (!this.state.selectedIcd10s.some(i => i.icd10Code == code)) {
            let icds = [...this.state.suggestedIcd10s].filter(i => i.icd10Code !== code);
            let selected = [...this.state.selectedIcd10s];
            selected.push({icd10Code: code, description: desc, fullCode});
            this.setState({suggestedIcd10s: icds, selectedIcd10s: selected, hmoDxConfirmed: true}, () => this.setDirty(this.getSuggestions));
        }
    }

    removeSelectedIcd10 = (code, desc) => {
        let icds = [...this.state.suggestedIcd10s];
        let selected = [...this.state.selectedIcd10s].filter(i => i.icd10Code !== code);
        // only push it back onto the suggested list if it's not already there
        if (!this.state.suggestedIcd10s.some(i => i.icd10Code == code)) {
            icds.push({icd10Code: code, description: desc});
        }
        this.setState({suggestedIcd10s: icds, selectedIcd10s: selected}, () => this.setDirty(this.getSuggestions));
    }

    removeChronicIcd10 = (code, desc) => {
        let icds = [...this.state.chronicIcd10s].filter(i => i.icd10Code !== code);
        this.setState({chronicIcd10s: icds}, () => this.setDirty(this.getSuggestions));
    }

    flagOrderOption = (orderName, optionName, iconName, label, iconClass, iconColor) => {
        let meta = this.state.orderMetadata.find(m => m.name === orderName);
        if (meta.options) {
            meta.options.filter(o => o.optionName === optionName).forEach(oo => {
                if (!Array.isArray(oo.flags)) {
                    oo.flags = [];
                }
                if (!oo.flags.find(f => f.iconName === iconName)) {
                    oo.flags.push({iconName, label, iconClass, iconColor});
                }
            })
        }
    }

    unflagOrderOption = (orderName, optionName, iconName) => {
        let meta = this.state.orderMetadata.find(m => m.name === orderName);
        if (meta.options) {
            meta.options.filter(o => o.optionName === optionName).forEach(oo => {
                if (Array.isArray(oo.flags)) {
                    let index = oo.flags.findIndex(f => f.iconName === iconName);
                    if (index >= 0) {
                        oo.flags.splice(index, 1);
                    }
                }
            })
        }
    }

    orderChanged = (orderMetaId, checked) => {
        let orders = [...this.state.orders];
        let index = orders.findIndex(o => o.orderMetaId == orderMetaId);
        if (checked) {
            // ensure is present
            if (index == -1) {
                let newOrder = {orderMetaId, options: []};
                let meta = this.state.orderMetadata.find(m => m.id === orderMetaId);
                if (meta && meta.name === "EEG-Ambulatory") {
                    // Check ambulatory appointment stats
                    let rec = this.getAmbulatoryEEGRecommendation();
                    if (rec === "48") {
                        this.flagOrderOption("EEG-Ambulatory", "48 Hour, Video", "star-fill", "48H is currently preferred to help optimize the schedule.", null, "#f66d11");
                        this.flagOrderOption("EEG-Ambulatory", "48 Hour, No Video", "star-fill", "48H is currently preferred to help optimize the schedule.", null, "#f66d11");
                    } else {
                        this.unflagOrderOption("EEG-Ambulatory", "48 Hour, Video", "star-fill");
                        this.unflagOrderOption("EEG-Ambulatory", "48 Hour, No Video", "star-fill");
                    }
                    if (rec === "72") {
                        this.flagOrderOption("EEG-Ambulatory", "72 Hour, Video", "star-fill", "72H is currently preferred to help optimize the schedule.", null, "#f66d11");
                        this.flagOrderOption("EEG-Ambulatory", "72 Hour, No Video", "star-fill", "72H is currently preferred to help optimize the schedule.", null, "#f66d11");
                    } else {
                        this.unflagOrderOption("EEG-Ambulatory", "72 Hour, Video", "star-fill");
                        this.unflagOrderOption("EEG-Ambulatory", "72 Hour, No Video", "star-fill");
                    }
                }

                orders.push(newOrder);
            }
        } else if (index >= 0) {
            // remove
            orders.splice(index, 1);
        }
        this.setState({orders}, () => {this.setDirty(); this.evaluateSuperbillRules()});
    }

    getAmbulatoryEEGRecommendation = () => {
        let stats = this.state.ambulatoryEEGStats;
        if (stats) {
            if (stats.eeg48 && !stats.eeg72) {
                // 48 has a value but 72 doesn't
                return "72";
            } else if (stats.eeg72 && !stats.eeg48) {
                // 72 has value but 48 doesn't
                return "48";
            } else if (!stats.eeg72 && !stats.eeg48) {
                // both numbers blank/0
                return null;
            } else if (stats.eeg48 / stats.eeg72 <= stats.ratio) {
                // both have a value - check ratio
                return "48";
            } else {
                // both have value and above ratio
                return "72";
            }
        }
        // missing data
        return null;
    }

    optionChanged = (orderMetaId, optionId, value) => {
        let orders = [...this.state.orders];
        let order = orders.find(o => o.orderMetaId == orderMetaId);
        if (order) {
            // make a copy of the contents of the options array to trigger a state update
            let options = [...(order.options || [])];
            order.options = options;
            let option = options.find(opt => opt.optionId == optionId);
            if (!option) {
                option = {optionId, value};
                options.push(option);
            } else {
                option.value = value;
            }

            // Commenting out for now, biz is not sure they want this
            // let meta = this.state.orderMetadata.find(m => m.id === orderMetaId);
            // if (meta.name.includes("EMG") && value) {
            //     // order is EMG and option was selected - do special logic
            //     let optMeta = meta.options.find(o => o.optionId === optionId);
            //     let toAdd = null;
            //     if (optMeta.optionName.toLowerCase().includes("upper")) {
            //         // make sure SSEP Uppers is selected
            //         toAdd = this.state.orderMetadata.find(m => m.name === "SSEPUE");
            //     } else if (optMeta.optionName.toLowerCase().includes("lower")) {
            //         // make sure SSEP Lowers is selected
            //         toAdd = this.state.orderMetadata.find(m => m.name === "SSEPLE");
            //     }
            //     if (toAdd && !orders.find(o => o.orderMetaId === toAdd.id)) {
            //         orders.push({orderMetaId: toAdd.id, options: []});
            //     }
            // }

            this.setState({orders}, () => {this.setDirty(); this.evaluateSuperbillRules()});
        }
    }

    orderDueDateChanged = (orderMetaId, dueDate) => {
        // make a copy of the contents of the options array to trigger a state update
        let orders = [...this.state.orders];
        let order = orders.find(o => o.orderMetaId == orderMetaId);
        if (order) {
            order.dueDate = dueDate;
            this.setState({orders}, this.setDirty);
        }
    }

    optionDueDateChanged = (orderMetaId, optionId, dueDate) => {
        console.log(`option due date changed ${orderMetaId} ${optionId} ${dueDate}`);
        let orders = [...this.state.orders];
        let order = orders.find(o => o.orderMetaId === orderMetaId);
        if (order) {
            // make a copy of the contents of the options array to trigger a state update
            let options = [...(order.options || [])];
            let option = options.find(opt => opt.optionId === optionId);
            if (option) {
                option.optionDueDate = dueDate;
                order.options = options;
                this.setState({orders}, this.setDirty);
            }
        }
    }

    optionDxChanged = (orderMetaId, optionId, dx) => {
        let orders = [...this.state.orders];
        let order = orders.find(o => o.orderMetaId === orderMetaId);
        if (order) {
            // make a copy of the contents of the options array to trigger a state update
            let options = [...(order.options || [])];
            order.options = options;
            let option = options.find(opt => opt.optionId === optionId);
            if (!option) {
                option = {optionId, dx};
                options.push(option);
            } else {
                option.dx = dx;
            }
            this.setState({orders}, this.setDirty);
        }
    }

    gatherDiagnoses = () => {
        return this.state.patient ? [
            ...this.state.patient.diagnoses.map(d => ({...d, description: d.description || d.icdDescription})),
            ...(this.state.selectedIcd10s || []),
            ...(this.state.chronicIcd10s || [])
        ] : [];
    }

    save = (submit, refreshSuggestions, reload) => {
        if (this.autosaveTimer) window.clearTimeout(this.autosaveTimer);

        if (submit) {
            if (!this.validate()) return;

            // see if there are any suggested orders that weren't selected
            let unordered = this.getUnorderedSuggestions();
            if (unordered && unordered.length > 0) {
                this.setState({showConfirmModal: true})
            } else {
                return this.doSave(submit, refreshSuggestions, reload);
            }
        } else {
            return this.doSave(submit, refreshSuggestions, reload);
        }
    }

    doSave = (submit, refreshSuggestions, reload) => {
        return this.setState({saving: true}, () => {
            let sb = this.state.superbill;
            if (submit) {
                sb.status = "SUBMITTED";
            }
            sb.diagnoses = this.state.selectedIcd10s;
            sb.orders = this.state.orders;
            sb.suggestedOrders = this.state.suggestedOrders;
            sb.chronicIcd10s = this.state.chronicIcd10s;

            return this.context.apiRequest("POST", "/superbill", sb)
                .then((d) => {
                    if (reload) {
                        this.loadData();
                    } else if (d && d.data) {
                        let sb = this.state.superbill;
                        sb.id = d.data.id;
                        this.setState({superbill: sb}, () => {
                            this.setState({dirty: false, saved: true, saving: false}, submit ? this.goBack : null)
                        });
                        if (refreshSuggestions) this.getSuggestions();
                    }
                });
        })
    }

    unsubmit = () => {
        this.context.confirm(() => {
            this.setSuperbillField("status", "NEW",  () => this.save(false, true, true));
        }, "Are you sure you want to unsubmit this superbill? Any related tasks on the Auth Board will be cancelled.", "Confirm Unsubmit")
    }

    getOrderValue = (orderMetaId) => {
        let o = this.state.orders.find(o => o.orderMetaId == orderMetaId);
        if (o && o.ordered != 0) {
            // not explicitly set to 0 - return the order
            return o;
        }
        return null;
    }

    getOrderDueDate = (orderMetaId) => {
        let o = this.state.orders.find(o => o.orderMetaId == orderMetaId);
        if (o && o.ordered != 0) {
            // not explicitly set to 0 - return the order
            return o.dueDate || null;
        }
        return null;
    }

    isOrderPtDeclined = (orderMetaId) => {
        let order = this.state.orders.find(o => o.orderMetaId == orderMetaId);
        // console.log(`isOrderPtDeclined: `, order);
        if (order) return order.patientDeclined || false;
        return false;
    }

    toggleOrderPtDeclined = (orderMetaId, declined) => {
        // console.log(`pt declined: ${orderMetaId}, ${declined}`);
        let orders = [...this.state.orders];
        let order = orders.find(o => o.orderMetaId == orderMetaId);
        if (order) {
            order.patientDeclined = declined;
        } else {
            orders.push({orderMetaId, options: [], patientDeclined: declined, ordered: false});
        }
        this.setState({orders}, () => console.log(this.state.orders));
    }

    findSuggestedOrder = (orderMetaId) => {
        return this.state.suggestedOrders.find(o => o.orderMetaId == orderMetaId);
    }

    getSuggestedOptions = (orderMetaId) => {
        let order = this.state.suggestedOrders.find(o => o.orderMetaId == orderMetaId);
        return order ? order.options || [] : [];
    }

    generateDxSourceText = (d) => {
        if (d && d.source) {
            let src = d.source === "REFERRAL" ? "Ref" : "CNS";
            if (d.timestamp) {
                src += " " + moment(d.timestamp).format("M/D/YY");
            }
            return src;
        }
        return "";
    }

    printForm = () => {
        window.print();
        this.context.apiRequest("POST", `/superbill/${this.state.superbill.id}/printed`)
    }

    toggleStat = () => {
        this.setSuperbillField("stat", this.state.superbill.stat ? 0 : 1, this.setDirty);
    }

    pickListItemSelected = (fieldName, val) => {
        let current = this.state.superbill[fieldName] || "";
        if (current && current.length > 0) current += "\n";
        current += val;
        this.setSuperbillField(fieldName, current);
    }

    pickListItemDeselected = (fieldName, val) => {
        let current = this.state.superbill[fieldName] || "";
        current = current.replaceAll(val, "");
        current = current.replaceAll("\n\n", "\n");
        current = current.trim();
        this.setSuperbillField(fieldName, current);
    }

    icdTextForClipboard = () => {
        let p = this.state.patient;
        let list = p.diagnoses ? p.diagnoses.filter(d => d.icd10Code).map(d => d.fullCode + " " + (d.description || d.icdDescription)) : [];
        list = list.concat(this.state.selectedIcd10s.map(i => i.fullCode + " " + i.description));
        return list.join("\n");
    }

    chronicIcdTextForClipboard = () => {
        let icds = this.state.chronicIcd10s;
        let list = icds ? icds.map(d => d.fullCode + " " + (d.description || d.icdDescription)) : [];
        return list.join("\n");
    }

    getUnorderedSuggestions = () => {
        let orderedIds = this.state.orders.filter(o => (o.ordered == undefined || o.ordered == 1)).map(o => o.orderMetaId);
        return this.state.suggestedOrders.filter(o => o.internal == 1 && !orderedIds.includes(o.orderMetaId));
    }

    shouldShowChronic = () => {
        let appts = this.state.patient ? this.state.patient.appointments : null;
        if (appts) {
            return appts.some(a => this.chronicAppts.includes(a.reasonCode));
        }
        return false;
    }

    canUnsubmit = () => {
        let sb = this.state.superbill;
        if (sb) {
            // unsubmit is allowed if status is SUBMITTED, we're not in read only mode, and the last update was in the last 24 hrs
            return (sb. status === "SUBMITTED"
                && !this.state.readOnly
                && moment(sb.updated).isAfter(moment().subtract(24, "hours")));
        }
        return false;
    }

    evaluateSuperbillRules = () => {
        let rules = [...this.state.superbillRules];
        let selectedOrders = this.state.orders;
        // Find all rules that apply to the selected orders, and either aren't tied to an option or the option is selected.
        let changed = false;
        for (const r of rules) {
            if (!this.ruleInsuranceApplies(r)) continue;
            // If the rule doesn't have an order or option, it applies to the entire superbill. Otherwise, check if the order and option are selected.
            let triggered = (!r.orderMetaId && !r.orderOptionMetaId) 
                || (r.orderMetaId && selectedOrders.some(o => o.orderMetaId == r.orderMetaId && (r.orderOptionMetaId == null || o.options.some(o2 => o2.optionId == r.orderOptionMetaId && o2.value))));
            if (triggered && !r.triggered) {
                changed = true;
                r.triggered = true;
                // Triggering for the first time - call the ruleTriggered callback
                this.ruleTriggered(r);
            } else if (!triggered && r.triggered) {
                changed = true;
                r.triggered = false;
            }
        }
        if (changed) {
            this.setState({superbillRules: rules});
        }
    }

    ruleInsuranceApplies = (rule) => {
        // rule doesn't have any insurance requirements - it applies
        if (!rule.insurancePlanCodes && !rule.insuranceCompanyCodes) return true;
        if (rule.insurancePlanCodes) {
            let planCodes = rule.insurancePlanCodes.split(",");
            let plan = this.state.patient.insurances.find(i => planCodes.includes(i.planCode));
            if (plan) return true;
            return false;
        }
        if (rule.insuranceCompanyCodes) {
            let companyCodes = rule.insuranceCompanyCodes.split(",");
            let company = this.state.patient.insurances.find(i => companyCodes.includes(i.companyCode));
            if (company) return true;
            return false;
        }
        return false;
    }

    ruleTriggered = (rule) => {
        console.log(`ruleTriggered: ${rule.action}`);
        switch(rule.action) {
            // Right now only supported action is CONFIRM. Might have others in the future like "WARN" or "REMOVE".
            case "CONFIRM":
                // confirm = (cb, prompt, title, okButtonText, cancelButtonText, size)
                // Confirm logic is intentionally backwards here - the callback will be called if they click "No".
                this.context.confirm(() => {this.removeOrderOrOption(rule.orderMetaId, rule.orderOptionMetaId); rule.triggered = false;}, rule.param1, rule.param2 || "Confirm", "No", "Yes", "md");
                break;
            case "TRACK_FACETIME":
                this.setState({showFacetimeTracking: true});
                break;
            default:
                console.log(`Unknown rule action: ${rule.action}`);
        }
    }

    removeOrderOrOption = (orderMetaId, optionId) => {
        let orders = [...this.state.orders];
        if (optionId) {
            let order = orders.find(o => o.orderMetaId == orderMetaId);
            order.options = order.options.filter(o => o.optionId != optionId);
        }
        else if (orderMetaId) {
            orders = orders.filter(o => o.orderMetaId != orderMetaId);
        }
        this.setState({orders}, this.setDirty);
    }

    setFacetimeStart = (e) => {
        this.setFacetime("facetimeStart", e);
    }

    setFacetimeEnd = (e) => {
        this.setFacetime("facetimeEnd", e);
    }

    setFacetime = (field, obj) => {
        let val;
        if (obj === "now") {
            val = moment().format("HH:mm");    
        }
        else {
            val = obj.target.value ? obj.target.value.toUpperCase() : null;
        }
        console.log(`setFacetime: ${field} = ${val}`);
        this.setState({[`${field}Str`]: val});

        if (val && /^[0-9]{1,2}:[0-9]{2}$/.test(val)) {
            this.setSuperbillField(field, this.timeOnApptDate(val));
        } else {
            this.setSuperbillField(field, null);
        }
    }

    timeOnApptDate = (timeStr) => {
        let timeMoment = timeStr === "now" ? moment() : moment(timeStr, "HH:mm");
        let apptDate = moment(this.state.superbill.appointmentDate);
        return timeMoment.year(apptDate.year()).month(apptDate.month()).date(apptDate.date()).toDate();
    }

    render() {
        let p = this.state.patient;
        let hd = p ? p.historyDiagnoses : null;
        let meds = p ? p.medications : null;
        let sb = this.state.superbill;
        let allDiags = this.gatherDiagnoses();
        let internalOrders = this.state.orderMetadata.filter(o => o.internal == 1 && ((!this.state.readOnly && sb && sb.status != "SUBMITTED") || this.getOrderValue(o.id) != null));
        let selectedInternalOrders = internalOrders.filter(o=> this.getOrderValue(o.id) != null);
        let externalOrders = this.state.orderMetadata.filter(o => o.internal == 0 && ((!this.state.readOnly && sb && sb.status != "SUBMITTED") || this.getOrderValue(o.id) != null));
        let selectedExternalOrders = externalOrders.filter(o=> this.getOrderValue(o.id) != null);
        let unordered = this.getUnorderedSuggestions();
        let eegRec = this.getAmbulatoryEEGRecommendation();
        let confirmRulesTriggered = this.state.superbillRules.some(r => r.triggered && r.action == "CONFIRM");


        return <Container fluid style={{height: "100%"}}>
            {unordered && unordered.length > 0 && <SuperbillConfirmModal
                unordered={unordered}
                isOrderPtDeclined={this.isOrderPtDeclined}
                toggleOrderPtDeclined={this.toggleOrderPtDeclined}
                show={this.state.showConfirmModal}
                onConfirm={() => this.doSave(true, false, false)}
                onCancel={() => this.setState({showConfirmModal: false})}/>
            }
            <InterimSummaryModal show={this.state.showInterimSummary} loading={this.state.interimSummariesLoading}
                                 summaries={this.state.interimSummaries} patient={this.state.patient}
                                 onHide={() => this.setState({showInterimSummary: false})}/>
            { p && sb && <Form noValidate={true} validated={this.state.validated} id={"sb-form"} onSubmit={e => {e.preventDefault(); e.stopPropagation();}}>
                <ConfirmModal okLabel={"Discard Changes"} cancelLabel={"Stay Here"} onOk={() => this.goBack(true)}
                              title={"Unsaved Changes"} prompt={"Leave this page? You have unsaved changes."}
                              show={this.state.showLeaveModal} onCancel={() => this.setState({showLeaveModal: false})}/>
                <Row className={"print-only mt-3"}>
                    <Col>
                        <h2>Superbill Order Form</h2>
                    </Col>
                </Row>
                <Row className={"mb-2 no-print"}>
                    <Col md={0} lg={4}></Col>
                    <Col md={4} lg={4} className={"text-center"}>
                        {eegRec && <span className={"text-secondary small"}>First Available Ambulatory EEG: {eegRec === "48" ? "48H" : "72H"}</span>}
                    </Col>
                    <Col className={"text-right"} md={8} lg={4}>
                        {!this.state.dirty && this.state.saved && <span className={"text-success d-inline-block"}>Saved <Glyph name={"check-lg"}/></span>}

                        {this.state.invalid && <span className={"text-danger ml-2"}>Please fill out all required fields before submitting.</span>}

                        {confirmRulesTriggered ? <Glyph name={"exclamation-triangle-fill"} className={"text-warning ml-2"}/> : <></>}

                        <Button className={"ml-3"} type={"button"} variant={"secondary"} onClick={() => this.goBack(false)}>
                            <Glyph name={"arrow-left"} label={"Back to list"}/> Back
                        </Button>

                        {sb.id && <Button type={"button"} variant={"secondary"} className={"ml-3"} onClick={this.printForm}>
                            <Glyph name={"printer"} label={"Print"}/> Print
                        </Button>}

                        {sb.status != "SUBMITTED" && !this.state.readOnly &&
                            <Button type={"button"} variant={"secondary"} className={"ml-3"}
                                    onClick={() => this.save(true)} disabled={this.state.saving}>
                                <Glyph name={"send-check"}/> Submit
                            </Button>}

                        {this.canUnsubmit() &&
                            <Button type={"button"} variant={"secondary"} className={"ml-3"}
                                    onClick={this.unsubmit} disabled={this.state.saving}>
                                <Glyph name={"arrow-counterclockwise"}/> Unsubmit
                            </Button>}
                    </Col>
                </Row>
                <fieldset disabled={this.state.readOnly || sb.status == "SUBMITTED"}>
                    <Row>
                        <Col md={12}>
                            <Card className={"mb-2 superbill-a"}>
                                <Card.Body className={"p-2"}>
                                    {!this.state.readOnly &&
                                        <Button as={"a"} href={"#"} role={"button"} variant={"secondary"} size={"sm"}
                                                className={"no-print interim-summary-open"}
                                                onClick={this.openInterimSummary}>
                                            Interim Summary
                                        </Button>}
                                    <Row>
                                        <Col sm={2} lg={2}><FormLabel className={"font-weight-bold"}>Ordering Provider:</FormLabel></Col>
                                        <Col sm={6} lg={4}>
                                            <FormControl isInvalid={this.state.validated && this.state.superbill && !this.state.superbill.providerId} required size={"sm"} className={"mb-2"} name={"providerId"} as={"select"} custom value={sb.providerId || ""} onChange={this.superbillFieldChanged}>
                                                <option value={""} disabled>Select a provider</option>
                                                {
                                                    this.state.internalProviders.map(p => <option key={p.providerId} value={p.providerId}>
                                                        {p.firstName} {p.lastName}{`${p.titleCode ? ' '+p.titleCode : ''}`}
                                                    </option>)
                                                }
                                            </FormControl>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col sm={2} lg={2}><FormLabel className={"font-weight-bold"}>Patient Name:</FormLabel></Col>
                                        <Col sm={4} lg={4}>{p.firstName} {p.lastName}</Col>
                                        <Col sm={2} lg={2}><FormLabel
                                            className={"font-weight-bold"}>DOB:</FormLabel></Col>
                                        <Col sm={4} lg={4}>{moment(p.dob).format("M/D/YYYY")}</Col>
                                    </Row>
                                    <Row>
                                        <Col sm={2} lg={2}><FormLabel className={"font-weight-bold"}>Account Number:</FormLabel></Col>
                                        <Col sm={4} lg={4}>{p.externalPatientId}</Col>
                                        <Col sm={2} lg={2}><FormLabel className={"font-weight-bold"}>Date of Service:</FormLabel></Col>
                                        <Col sm={4} lg={4}>{moment(sb.appointmentDate).format("M/D/YYYY")}</Col>
                                    </Row>
                                    <Row>
                                        <Col sm={2} lg={2}><FormLabel className={"font-weight-bold"}>
                                            Insurance plan(s):
                                            {p.hmo ? <span className={"text-danger ml-2"}><Glyph name={"exclamation-circle"}/> HMO</span> : <></>}
                                        </FormLabel></Col>
                                        <Col sm={4} lg={4}>
                                            {
                                                p.insurances && p.insurances.length > 0 ? <ol className={"pl-3"}>
                                                    {
                                                        p.insurances.map(i => <li
                                                            key={'ins' + i.id}>{i.companyName} ({i.planCode})</li>)
                                                    }
                                                </ol> : <>(No plans on file)</>
                                            }
                                        </Col>
                                        <Col sm={2} lg={2}><FormLabel className={"font-weight-bold"}>Appts Today:</FormLabel></Col>
                                        <Col sm={4} lg={4}>
                                            <Table size={"sm"} borderless>
                                                <tbody>
                                                {
                                                    p.appointments && p.appointments.length > 0 ? p.appointments.map(a =>
                                                        <tr key={a.appointmentId}>
                                                            <td>{moment(a.appointmentDateTime).format("h:mm A")}</td>
                                                            <td>{a.reasonCode}</td>
                                                            <td>{a.providerLast}</td>
                                                        </tr>) : <>(No appointments)</>
                                                }
                                                </tbody>
                                            </Table>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col sm={2} lg={2} className={"font-weight-bold"}>Ref Provider:</Col>
                                        <Col sm={4} lg={4}>
                                            {
                                                p.referral && p.referral.provider1 ? <>{p.referral.provider1First} {p.referral.provider1.provider1Last}</> : <>(None)</>
                                            }
                                        </Col>
                                        {
                                            this.state.showFacetimeTracking && <>
                                                <Col sm={2} lg={2} className={"font-weight-bold"}>Time With Patient:</Col>
                                                <Col sm={4} lg={4}> 
                                                    <Row className={this.state.readOnly || sb.status === "SUBMITTED" ? "" : "print-only"}>
                                                        <Col>{sb.facetimeStart ? moment(sb.facetimeStart).format("h:mm A") : ""} - {sb.facetimeEnd ? moment(sb.facetimeEnd).format("h:mm A") : ""}</Col>
                                                    </Row>
                                                    {!this.state.readOnly && sb.status !== "SUBMITTED" && <Row className={"no-print"}>
                                                        <Col md={12} lg={6} className={"text-nowrap mb-1"}>
                                                            <Button size={"sm"} className={"mr-2"} variant={"success"} onClick={() => this.setFacetimeStart("now")}>Start</Button>
                                                            <FormControl type={"time"} className={"d-inline-block"} style={{width: "100px"}} size={"sm"} value={this.state.facetimeStartStr} onChange={this.setFacetimeStart}/>
                                                            <FormControl.Feedback type="invalid">
                                                                H:MM AM/PM, e.g. 1:30 PM.
                                                            </FormControl.Feedback>
                                                        </Col>
                                                        <Col md={12} lg={6} className={"text-nowrap mb-1"}>
                                                            <Button size={"sm"} className={"mr-2"} variant={"danger"} onClick={() => this.setFacetimeEnd("now")}>End</Button>
                                                            <FormControl type={"time"} className={"d-inline-block"} style={{width: "100px"}} size={"sm"} value={this.state.facetimeEndStr} onChange={this.setFacetimeEnd}/>
                                                            <FormControl.Feedback type="invalid">
                                                                H:MM AM/PM, e.g. 1:30 PM.
                                                            </FormControl.Feedback>
                                                        </Col>
                                                    </Row>}
                                                </Col>
                                            </>
                                        }
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    {
                        hd && hd.length > 0 && <Row>
                            <Col md={12}>
                                <Card className={"mb-2 superbill-e"}>
                                    <Card.Body className={"p-2"}>
                                        <Row>
                                            <Col>
                                                <FormLabel className={"font-weight-bold mb-0"}>Review of Records: ICD10s and Medications</FormLabel>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col>
                                                <small className={"type-italic"}>(Chronic Conditions, Past medical history, Incidental findings, Secondary diagnoses)</small>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col lg={6} sm={12}>
                                                <Table size={"sm"} borderless>
                                                    <thead>
                                                    <tr>
                                                        <th>Date Reported</th>
                                                        <th>Condition</th>
                                                    </tr>
                                                    </thead>
                                                    <tbody>
                                                    {
                                                        hd.map((d,i) => <tr key={`hd-${i}`}>
                                                            <td>{moment(d.timestamp).format("M/D/YYYY")}</td>
                                                            {
                                                                d.otherCondition ? <td colSpan={2}>{d.otherCondition}</td>
                                                                    : <><td>{d.fullCode}</td><td>{d.description}</td></>
                                                            }
                                                        </tr>)
                                                    }
                                                    </tbody>
                                                </Table>
                                            </Col>
                                            <Col lg={6} sm={12}>
                                                <Table size={"sm"} borderless>
                                                    <thead>
                                                    <tr>
                                                        <th>Date Reported</th>
                                                        <th>Medication</th>
                                                    </tr>
                                                    </thead>
                                                    <tbody>
                                                    {
                                                        meds && meds.length > 0 ? meds.map((d,i) => <tr key={`meds-${i}`}>
                                                            <td>{moment(d.recordedAt).format("M/D/YYYY")}</td>
                                                            <td colSpan={2}>{d.text}</td>
                                                        </tr>) : <tr><td colSpan={2}>(No medications)</td></tr>
                                                    }
                                                    </tbody>
                                                </Table>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    }
                    <Row>
                        <Col>
                            <Card className={"superbill-b"}>
                                <Card.Body className={"p-2 mb-1"}>
                                    <Row>
                                        {!this.state.readOnly &&
                                            <Col md={6} className={"no-print"}>
                                                <FormLabel className={"font-weight-bold"}>Search Diagnoses</FormLabel>
                                                <ICD10Search id={"icd10-search"}
                                                             icd10Selected={this.icd10Selected}/>
                                                <FormLabel className={"font-weight-bold mt-2"}>Most Common Diagnoses for Similar Patients</FormLabel>
                                                <ListGroup variant={"flush"} style={{height: "200px", overflowY: "auto", backgroundColor: "#B0C286"}}>
                                                    {
                                                        this.state.suggestedIcd10s && this.state.suggestedIcd10s.map(i =>
                                                            <ListGroupItem className={"p-1"} key={i.icd10Code}>
                                                                <Row>
                                                                    <Col sm={3} className={"text-small"}>{i.fullCode}</Col>
                                                                    <Col sm={7}>{i.description}</Col>
                                                                    <Col sm={2} className={"text-right"}><Glyph
                                                                        name={"arrow-right"} className={"clickable mr-3"}
                                                                        onClick={() => this.icd10Selected(i.icd10Code, i.description, i.fullCode)}/></Col>
                                                                </Row>
                                                            </ListGroupItem>)
                                                    }
                                                </ListGroup>
                                            </Col>
                                        }
                                        <Col md={6}>
                                            <Row noGutters>
                                                <Col><FormLabel className={"font-weight-bold"}>Patient's Diagnoses</FormLabel></Col>
                                                <Col sm={"auto"}><CopyButton value={this.icdTextForClipboard()} label={"Copy to clipboard"}/></Col>
                                            </Row>

                                            <ListGroup variant={"flush"} style={{minHeight: "275px", overflowY: "auto"}} className={"no-print"}>
                                                {
                                                    p.diagnoses && p.diagnoses.map(i => <ListGroupItem className={"p-1"}
                                                                                                       key={i.icd10Code}>
                                                        <Row>
                                                            <Col sm={4} className={"text-small"}>
                                                                {i.fullCode || '(Ref Text)'}
                                                                {i.source && <>
                                                                    <br/>
                                                                    <small
                                                                        className={"text-secondary"}><i>{this.generateDxSourceText(i)}</i></small>
                                                                </>}
                                                            </Col>
                                                            <Col sm={6}>{i.description || i.icdDescription}</Col>
                                                            <Col sm={2} className={"text-right"}></Col>
                                                        </Row>
                                                    </ListGroupItem>)
                                                }
                                                {
                                                    this.state.selectedIcd10s && this.state.selectedIcd10s.map(i =>
                                                        <ListGroupItem className={"p-1"} key={i.icd10Code}>
                                                            <Row>
                                                                <Col sm={4} className={"text-small"}>{i.fullCode}</Col>
                                                                <Col sm={6}>{i.description}</Col>
                                                                <Col sm={2} className={"text-right"}>
                                                                    {!this.state.readOnly && sb.status !== "SUBMITTED" &&
                                                                        <Glyph name={"x-lg"}
                                                                               className={"clickable mr-3 no-print"}
                                                                               onClick={() => this.removeSelectedIcd10(i.icd10Code, i.description)}/>}
                                                                </Col>
                                                            </Row>
                                                        </ListGroupItem>)
                                                }
                                            </ListGroup>
                                            <Table className={"print-only"} borderless size={"sm"}>
                                                <tbody>
                                                {
                                                    p.diagnoses && p.diagnoses.map(i => <tr key={i.icd10Code}>
                                                            <td>
                                                                {i.fullCode || '(Ref Text)'}
                                                            </td>
                                                            <td>{i.description || i.icdDescription}</td>
                                                    </tr>)
                                                }
                                                {
                                                    this.state.selectedIcd10s && this.state.selectedIcd10s.map(i =>
                                                        <tr key={i.icd10Code}>
                                                                <td>{i.fullCode}</td>
                                                                <td>{i.description}</td>
                                                        </tr>)
                                                }
                                                </tbody>
                                            </Table>
                                        </Col>
                                    </Row>
                                    { this.shouldShowChronic() &&
                                    <Row className={"mt-3"}>
                                        {!this.state.readOnly &&
                                            <Col md={6} className={"no-print"}>
                                                <FormLabel className={"font-weight-bold"}>Search Chronic Conditions</FormLabel>
                                                <ICD10Search id={"chronic-icd10-search"}
                                                             icd10Selected={this.chronicIcd10Selected}/>
                                            </Col>
                                        }
                                        <Col md={6}>
                                            <Row noGutters>
                                                <Col><FormLabel className={"font-weight-bold"}>Patient's Chronic Conditions</FormLabel></Col>
                                                <Col sm={"auto"}><CopyButton value={this.chronicIcdTextForClipboard()} label={"Copy to clipboard"}/></Col>
                                            </Row>

                                            {
                                                this.state.chronicIcd10s.length == 0 ?
                                                    <Form.Check id={"no-chronic"} type={"switch"} checked={this.state.noChronic} onChange={() => this.setState({noChronic: !this.state.noChronic})} label={"No New Chronic Conditions"}/>
                                                    :
                                                    <ListGroup variant={"flush"} style={{overflowY: "auto"}} className={"no-print"}>
                                                        {
                                                            this.state.chronicIcd10s.map(i => <ListGroupItem className={"p-1"}
                                                                                                             key={"c_"+i.icd10Code}>
                                                                <Row>
                                                                    <Col sm={4} className={"text-small"}>
                                                                        {i.fullCode || '(Ref Text)'}
                                                                    </Col>
                                                                    <Col sm={6}>{i.description || i.icdDescription}</Col>
                                                                    <Col sm={2} className={"text-right"}>
                                                                        {!this.state.readOnly && sb.status !== "SUBMITTED" &&
                                                                            <Glyph name={"x-lg"}
                                                                                   className={"clickable mr-3 no-print"}
                                                                                   onClick={() => this.removeChronicIcd10(i.icd10Code, i.description)}/>}
                                                                    </Col>
                                                                </Row>
                                                            </ListGroupItem>)
                                                        }
                                                    </ListGroup>
                                            }
                                            <Table className={"print-only"} borderless size={"sm"}>
                                                <tbody>
                                                {
                                                    this.state.chronicIcd10s.map(i => <tr key={"cp_"+i.icd10Code}>
                                                            <td>{i.fullCode}</td>
                                                            <td>{i.description}</td>
                                                    </tr>)
                                                }
                                                </tbody>
                                            </Table>
                                            {/*<Form.Group>*/}
                                            {/*    <Form.Control className={"d-none"} isInvalid={this.state.validated && !this.state.noChronic && this.state.chronicIcd10s.length === 0} required defaultValue={this.state.noChronic || this.state.chronicIcd10s.length > 0 ? "ok" : ""}/>*/}
                                            {/*    <Form.Control.Feedback type="invalid">*/}
                                            {/*        You must choose chronic conditions or select 'No Chronic Conditions'.*/}
                                            {/*    </Form.Control.Feedback>*/}
                                            {/*</Form.Group>*/}
                                        </Col>
                                    </Row>
                                    }
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={12} lg={6}>
                            <Card className={"mt-2 superbill-d"}>
                                <Card.Body className={"p-2"}>
                                    <FormLabel className={"font-weight-bold"}>CNS Orders</FormLabel>
                                    <div className={"no-print"}>
                                        {
                                            internalOrders.length > 0 ?
                                                internalOrders.map(o =>
                                                    <SuperbillOrderItem key={o.name} metadata={o}
                                                                        value={this.getOrderValue(o.id)}
                                                                        dueDate={this.getOrderDueDate(o.id)}
                                                                        itemChanged={(checked) => this.orderChanged(o.id, checked)}
                                                                        optionChanged={(optionId, value) => this.optionChanged(o.id, optionId, value)}
                                                                        optionDxChanged={(optionId, dx) => this.optionDxChanged(o.id, optionId, dx)}
                                                                        dueDateChanged={(dueDate) => this.orderDueDateChanged(o.id, dueDate)}
                                                                        optionDueDateChanged={(optionId, dueDate) => this.optionDueDateChanged(o.id, optionId, dueDate)}
                                                                        diagnoses={allDiags}
                                                                        suggested={this.findSuggestedOrder(o.id)}
                                                                        suggestedOptions={this.getSuggestedOptions(o.id)}
                                                                        displayOnly={this.state.readOnly}
                                                                        disabled={sb && sb.status == "SUBMITTED"}/>)
                                                : <Row><Col>None</Col></Row>
                                        }
                                    </div>
                                    <div className={"print-only"}>
                                        {
                                            // For print, only show orders that were selected
                                            selectedInternalOrders.length > 0 ? <ul>
                                                {selectedInternalOrders.map(o => <li key={o.name + "_"}>
                                                    <SuperbillOrderItem metadata={o}
                                                                        displayOnly={true}
                                                                        value={this.getOrderValue(o.id)}
                                                                        dueDate={this.getOrderDueDate(o.id)}
                                                                        diagnoses={allDiags}/></li>)}
                                            </ul>  : <Row><Col>None</Col></Row>
                                        }
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col sm={12} lg={6}>
                            <Card className={"mt-2 superbill-d"}>
                                <Card.Body className={"p-2"}>
                                    <FormLabel className={"font-weight-bold"}>Outside Orders</FormLabel>
                                    <div className={"no-print"}>
                                        {
                                            externalOrders.length > 0 ?
                                                externalOrders.map(o =>
                                                    <SuperbillOrderItem key={o.name} metadata={o}
                                                                        value={this.getOrderValue(o.id)}
                                                                        dueDate={this.getOrderDueDate(o.id)}
                                                                        itemChanged={(checked) => this.orderChanged(o.id, checked)}
                                                                        optionChanged={(optionId, value) => this.optionChanged(o.id, optionId, value)}
                                                                        optionDxChanged={(optionId, dx) => this.optionDxChanged(o.id, optionId, dx)}
                                                                        dueDateChanged={(dueDate) => this.orderDueDateChanged(o.id, dueDate)}
                                                                        optionDueDateChanged={(optionId, dueDate) => this.optionDueDateChanged(o.id, optionId, dueDate)}
                                                                        diagnoses={allDiags}
                                                                        suggested={this.findSuggestedOrder(o.id)}
                                                                        suggestedOptions={this.getSuggestedOptions(o.id)}
                                                                        displayOnly={this.state.readOnly}
                                                                        disabled={sb && sb.status == "SUBMITTED"}/>)
                                                : <Row><Col>None</Col></Row>
                                        }
                                    </div>
                                    <div className={"print-only"}>
                                        {
                                            // For print, only show orders that were selected
                                            selectedExternalOrders.length > 0 ? <ul>
                                                {
                                                    selectedExternalOrders.map(o => <li key={o.name}>
                                                        <SuperbillOrderItem metadata={o}
                                                                            displayOnly={true}
                                                                            value={this.getOrderValue(o.id)}
                                                                            dueDate={this.getOrderDueDate(o.id)}
                                                                            diagnoses={allDiags}/></li>)
                                                }
                                                </ul> : <Row><Col>None</Col></Row>

                                        }
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    <Row className={"no-print"}>
                        <Col sm={12} className={"type-italic"}>
                            <Glyph name={"stars"} className={"ml-2 text-success"}/> = <b>procedure</b> is
                            suggested for similar patients.</Col>
                    </Row>
                    <Row>
                        <Col sm={12}>
                            <Card className={"mt-2 superbill-c"}>
                                <Card.Body className={"p-2"}>
                                    <Row>
                                        <FormGroup controlId={"prescriptionComments"} as={Col} sm={6} lg={3}>
                                            <Row noGutters>
                                                <Col><FormLabel className={"font-weight-bold"}>Prescriptions: </FormLabel></Col>
                                                <Col className={"text-right"}><PickList listName={"Superbill-Prescriptions"} controlValue={sb.prescriptionComments} onItemSelected={(val) => this.pickListItemSelected("prescriptionComments", val)} onItemDeselected={(val) => this.pickListItemDeselected("prescriptionComments", val)}/></Col>
                                            </Row>
                                            <div className={"print-only"}>{nlToBr(sb.prescriptionComments)}</div>
                                            <FormControl as={"textarea"} rows={3} name={"prescriptionComments"}
                                                         value={sb.prescriptionComments || ""}
                                                         onChange={this.superbillFieldChanged}
                                                         className={"no-print"}/>
                                        </FormGroup>

                                         <FormGroup controlId={"labOrders"} as={Col} sm={6} lg={3}>
                                            <Row noGutters>
                                                <Col><FormLabel className={"font-weight-bold"}>Labs: </FormLabel></Col>
                                                <Col className={"text-right"}>
                                                    <CopyButton value={sb.labOrders || ""} label={"Copy to clipboard"} className={"mr-2"}/>
                                                    <PickList listName={"Superbill-Labs"} controlValue={sb.labOrders} onItemSelected={(val) => this.pickListItemSelected("labOrders", val)} onItemDeselected={(val) => this.pickListItemDeselected("labOrders", val)}/>
                                                </Col>
                                            </Row>
                                            <div className={"print-only"}>{nlToBr(sb.labOrders)}</div>
                                            <div className={"print-only"}>{sb.labOrdersDx ? `DX: ${sb.labOrdersDx}` : ``}</div>
                                            <FormControl as={"textarea"} rows={2} name={"labOrders"}
                                                         value={sb.labOrders || ""}
                                                         onChange={this.superbillFieldChanged}
                                                         className={"no-print"}/>
                                             <SuperbillDxInput className={"no-print"} allIcds={allDiags} value={sb.labOrdersDx} onDxChanged={(csv) => this.setSuperbillField("labOrdersDx", csv)}/>
                                        </FormGroup>

                                        <FormGroup controlId={"generalComments"} as={Col} sm={6} lg={3}>
                                            <Row noGutters>
                                                <Col><FormLabel className={"font-weight-bold"}>Additional Testing: </FormLabel></Col>
                                                <Col className={"text-right"} sm={"auto"}><PickList listName={"Superbill-Additional-Testing"} controlValue={sb.generalComments} onItemSelected={(val) => this.pickListItemSelected("generalComments", val)} onItemDeselected={(val) => this.pickListItemDeselected("generalComments", val)}/></Col>
                                            </Row>
                                            <div className={"print-only"}>{nlToBr(sb.generalComments)}</div>
                                            <FormControl as={"textarea"} rows={3} name={"generalComments"}
                                                         value={sb.generalComments || ""}
                                                         onChange={this.superbillFieldChanged}
                                                         className={"no-print"}/>
                                        </FormGroup>

                                        <FormGroup controlId={"recordsComments"} as={Col} sm={6} lg={3}>
                                            <Row noGutters>
                                                <Col><FormLabel className={"font-weight-bold"}>Request Records: </FormLabel></Col>
                                                <Col className={"text-right"} sm={"auto"}><PickList listName={"Superbill-Request-Records"} controlValue={sb.recordsComments} onItemSelected={(val) => this.pickListItemSelected("recordsComments", val)} onItemDeselected={(val) => this.pickListItemDeselected("recordsComments", val)}/></Col>
                                            </Row>
                                            <div className={"print-only"}>{nlToBr(sb.recordsComments)}</div>
                                            <FormControl as={"textarea"} rows={3} name={"recordsComments"}
                                                         value={sb.recordsComments || ""}
                                                         onChange={this.superbillFieldChanged}
                                                         className={"no-print"}/>
                                        </FormGroup>
                                    </Row>
                                    <Row>
                                        <FormGroup controlId={"additionalComments"} as={Col} sm={6} lg={3}>
                                            <Row noGutters>
                                                <Col><FormLabel className={"font-weight-bold"}>Additional Comments: </FormLabel></Col>
                                                <Col className={"text-right"} sm={"auto"}><PickList listName={"Superbill-Additional-Comments"} controlValue={sb.additionalComments} onItemSelected={(val) => this.pickListItemSelected("additionalComments", val)} onItemDeselected={(val) => this.pickListItemDeselected("additionalComments", val)}/></Col>
                                            </Row>
                                            <div className={"print-only"}>{nlToBr(sb.additionalComments)}</div>
                                            <FormControl as={"textarea"} rows={3} name={"additionalComments"}
                                                         value={sb.additionalComments || ""}
                                                         onChange={this.superbillFieldChanged}
                                                         className={"no-print"}/>
                                        </FormGroup>
                                        <FormGroup controlId={"followUp"} as={Col} sm={6} lg={3}>
                                            <FormLabel className={"font-weight-bold"}>Follow Up:</FormLabel>
                                            <div className={"print-only"}>{nlToBr(sb.followUp)}</div>
                                            <InputGroup className={"no-print"}>
                                                <FormControl maxLength={255} name={"followUp"}
                                                             value={sb.followUp || ""}
                                                             onChange={this.superbillFieldChanged}
                                                             className={"no-print"}
                                                             placeholder={"Type here or select"}/>

                                                <DropdownButton
                                                    as={InputGroup.Append}
                                                    variant="outline-secondary"
                                                    title="Select"
                                                    id="follow-up-dropdown"
                                                >
                                                    {
                                                        this.followUpOptions.map(o => <DropdownItem key={o} onClick={() => this.setSuperbillField("followUp", o)}>{o}</DropdownItem>)
                                                    }
                                                </DropdownButton>
                                            </InputGroup>

                                        </FormGroup>
                                        <FormGroup controlId={"stat"} as={Col} sm={6} lg={3}>
                                            <FormLabel className={"font-weight-bold"}>Priority:</FormLabel>
                                            <div className={"print-only"}>{sb.stat ? "STAT" : "Normal"}</div>
                                            <div>
                                                <ButtonGroup toggle className="no-print">
                                                    <ToggleButton
                                                        type="checkbox"
                                                        variant={sb.stat ? "danger" : "secondary"}
                                                        checked={sb.stat}
                                                        onChange={this.toggleStat}
                                                    >
                                                        {sb.stat ? "STAT" : "Normal"}
                                                    </ToggleButton>
                                                </ButtonGroup>
                                            </div>
                                        </FormGroup>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </fieldset>
                    {
                        !this.state.readOnly ?
                        <Row className={"mt-3 mb-4 no-print"}>
                            <Col sm={12} className={"text-right"}>
                                {!this.state.dirty && this.state.saved &&
                                    <span className={"text-success d-inline-block"}>Saved <Glyph
                                        name={"check-lg"}/></span>}

                                {this.state.invalid && <span className={"text-danger ml-2"}>Please fill out all required fields before submitting.</span>}

                                {confirmRulesTriggered ? <Glyph name={"exclamation-triangle-fill"} className={"text-warning ml-2"}/> : <></>}

                                <Button className={"ml-3"} type={"button"} variant={"secondary"}
                                        onClick={() => this.goBack(false)}>
                                    <Glyph name={"arrow-left"} label={"Back to list"}/> Back
                                </Button>

                                <Button type={"button"} variant={"secondary"} className={"ml-3"}
                                        onClick={this.printForm}>
                                    <Glyph name={"printer"} label={"Print"}/> Print
                                </Button>

                                {sb.status != "SUBMITTED" &&
                                    <Button type={"button"} variant={"secondary"} className={"ml-3"}
                                         onClick={() => this.save(true)} disabled={this.state.saving}>
                                    <Glyph name={"send-check"}/> Submit
                                </Button>}

                                {sb.status == "SUBMITTED" &&
                                    <Button type={"button"} variant={"secondary"} className={"ml-3"}
                                            onClick={this.unsubmit} disabled={this.state.saving}>
                                        <Glyph name={"arrow-counterclockwise"}/> Unsubmit
                                    </Button>}
                            </Col>
                        </Row> : <></>
                    }
                    {/*Spacer for scrolling*/}
                    <Row>
                        <Col>&nbsp;</Col>
                    </Row>
            </Form>}
        </Container>
    }
}